// One typed message at the scene-1 pause: the user's bubble, the assistant reply (mw /chat decides question / redo / continue),
// format/style option cards (thumbnail, Recommended, See all) and the confirm card. Nothing is rebuilt until a CURRENT confirm
// button is clicked: only the newest reply is live; older rows, a used row and a build that left the pause are all disabled.
import React, { useState } from "react";
import i18n from "../../../locales/i18n";
import { catalogThumb } from "./catalogThumb";
import BuildPickModal, { Thumb } from "./BuildPickModal";

const t = (k, o) => i18n.t(`aideaChat.buildNotice.${k}`, o);

// Identity of an option card (also what BuildThread stores as `picked` and sends back as `exclude`).
export const optId = (o) => (o.kind === "style" ? `style:${o.styleId}` : `format:${o.slug}`);

export default function BuildPauseReply({ row, paused, latest, onOption, onConfirm, onCancel }) {
  const { text, phase, reply, options, seeAll, confirm, status, err, picked } = row;
  const [seeOpen, setSeeOpen] = useState(false);
  const busy = status === "busy";
  const done = status === "done";
  const live = paused && latest && !busy && !done; // the confirm card
  const pickLive = live && !picked; // option cards and See all: one pick per reply, then they freeze
  const list = options
    ? [...options].sort((a, b) => Number(!!b.recommended) - Number(!!a.recommended))
    : [];
  const recommended = {};
  list.forEach((o) => {
    if (o.recommended) recommended[o.kind] = o.kind === "style" ? o.styleId : o.slug;
  });
  return (
    <>
      <div className="tw-flex tw-justify-end tw-py-2">
        <div className="tw-max-w-[85%] tw-rounded-2xl tw-bg-[var(--ac-surface)] tw-px-4 tw-py-2.5 tw-text-[14px] tw-leading-relaxed tw-text-[var(--ac-fg)] tw-shadow-sm tw-ring-1 tw-ring-[var(--ac-border)] tw-whitespace-pre-wrap tw-break-words">
          {text}
        </div>
      </div>
      <div className="tw-py-2 tw-text-[14px] tw-leading-relaxed tw-text-[var(--ac-text)]">
        {phase === "thinking" ? t("chatThinking") : reply}
        {done && <div>{t("improving")}</div>}
        {err && (
          <div role="alert" className="aidea-build-notice-error">
            {t(err)}
          </div>
        )}
      </div>
      {list.length > 0 && (
        <div className="tw-flex tw-flex-col tw-gap-2 tw-py-1">
          {list.map((o) => {
            const id = optId(o);
            const thumb = o.kind === "format" ? catalogThumb(o.slug, o.variant || 1) : o.previewUrl;
            return (
              <div
                key={id}
                className={`tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-p-2 tw-ring-1 ${picked === id ? "tw-ring-2 tw-ring-[var(--ac-accent)]" : "tw-ring-[var(--ac-border)]"}`}
              >
                <Thumb
                  src={thumb}
                  kind={o.kind}
                  className="tw-h-12 tw-w-20 tw-flex-none tw-rounded-md tw-object-cover"
                />
                <div className="tw-min-w-0 tw-flex-1 tw-text-[13px]">
                  <div className="tw-font-medium">
                    {o.label}
                    {o.recommended && (
                      <span className="tw-ml-2 tw-rounded-full tw-bg-[var(--ac-accent-tint)] tw-px-2 tw-py-0.5 tw-text-[11px] tw-text-[var(--ac-accent)]">
                        {t("recommended")}
                      </span>
                    )}
                  </div>
                  {o.why && <div>{o.why}</div>}
                </div>
                <button
                  type="button"
                  className="aidea-build-btn"
                  disabled={!pickLive}
                  onClick={() => onOption(row.key, o)}
                >
                  {picked === id ? t("optionSelected") : t("optionSelect")}
                </button>
              </div>
            );
          })}
        </div>
      )}
      {(list.length > 0 || seeAll) && (
        <div className="tw-py-1">
          <button
            type="button"
            className="aidea-build-btn aidea-build-btn-ghost"
            disabled={!pickLive}
            onClick={() => setSeeOpen(true)}
          >
            {t("seeAll")}
          </button>
          {seeOpen && (
            <BuildPickModal
              show
              initialKind={list[0] ? list[0].kind : "style"}
              recommended={recommended}
              onClose={() => setSeeOpen(false)}
              onPick={(o) => {
                setSeeOpen(false);
                onOption(row.key, o);
              }}
            />
          )}
        </div>
      )}
      {confirm && (
        <div className="aidea-build-notice" role="group">
          <div>
            {confirm.kind === "continue"
              ? t("continueConfirm")
              : confirm.name
                ? t("rebuildIn", { name: confirm.name })
                : t("rebuildWith", { note: confirm.note })}
          </div>
          {confirm.kind === "redo" && (
            <div className="aidea-build-notice-line">{t("rebuildCost")}</div>
          )}
          {confirm.pick && confirm.pick.style && (
            <div className="aidea-build-notice-line">{t("rebuildStyleAll")}</div>
          )}
          <div className="aidea-build-notice-actions">
            <button
              type="button"
              className="aidea-build-btn"
              disabled={!live}
              onClick={() => onConfirm(row.key)}
            >
              {done
                ? t("requested")
                : confirm.kind === "continue"
                  ? t("continue")
                  : confirm.fallback
                    ? t("rebuildThisNote")
                    : t("rebuild")}
            </button>
            <button
              type="button"
              className="aidea-build-btn aidea-build-btn-ghost"
              disabled={!live}
              onClick={() => onCancel(row.key)}
            >
              {confirm.fallback ? t("justAsk") : t("cancel")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
